import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IconAlertTriangle,
  IconBookmark,
  IconBrain,
  IconDownload,
  IconGavel,
  IconRefresh,
} from '@tabler/icons-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Btn from './ui/Btn';
import { ApiError, api } from '../lib/api';
import { toast } from '../stores/toaster';
import { parseResearchItems } from '../lib/researchItems';

/**
 * Onglet « Synthèse IA » — affiche les 4 recherches automatiques
 * persistées par l'orchestrateur Analyse IA :
 * - éléments à charge
 * - éléments à décharge
 * - synthèse du dossier
 * - questions juridiques (ex onglet « Alerte » → « Droit »)
 *
 * Items cochables (Phase 3) : l'avocat peut sélectionner les items
 * qu'il veut réutiliser dans rapport / conclusions / actes. Les
 * sélections sont persistées via PATCH /ai-research/<id>/picks et
 * consommées par les générateurs.
 */

type ResearchType = 'charges' | 'decharges' | 'synthese' | 'droit';

type ResearchSource =
  | {
      kind?: 'dossier';
      pdf_id: string;
      filename: string;
      page: number;
      score: number;
    }
  | {
      kind: 'corpus';
      source_id: string;
      source_type: 'doctrine' | 'jurisprudence_cass' | 'code_legal' | 'act_template';
      title: string;
      author: string | null;
      year: number | null;
      locator: string;
      score: number;
    };

type Research = {
  id: string;
  research_type: ResearchType;
  content_markdown: string;
  sources: ResearchSource[];
  checked_item_keys: string[];
  pdf_count_at_run: number;
  cost_eur: number | null;
  tokens_in: number | null;
  tokens_out: number | null;
  created_at: string;
};

const TYPE_LABELS: Record<ResearchType, string> = {
  charges: 'Éléments à charge',
  decharges: 'Éléments à décharge',
  synthese: 'Synthèse du dossier',
  droit: 'Droit (questions juridiques)',
};

const TYPE_ICONS: Record<ResearchType, JSX.Element> = {
  charges: <IconAlertTriangle size={16} stroke={1.6} className="text-cat-def" />,
  decharges: <IconBookmark size={16} stroke={1.6} className="text-cat-fav" />,
  synthese: <IconBrain size={16} stroke={1.6} className="text-accent" />,
  droit: <IconGavel size={16} stroke={1.6} className="text-cat-ver" />,
};

const ALL_TYPES: ResearchType[] = ['charges', 'decharges', 'synthese', 'droit'];

const MD_COMPONENTS = {
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 className="font-serif text-[18px] font-semibold text-text-primary mt-5 mb-2 first:mt-0">
      {children}
    </h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="font-serif text-[15px] font-semibold text-text-primary mt-4 mb-2">{children}</h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="text-[13.5px] font-semibold text-text-primary mt-3 mb-1.5">{children}</h3>
  ),
  p: ({ children }: { children?: React.ReactNode }) => <p className="my-2">{children}</p>,
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="list-disc pl-5 my-2 space-y-1">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="list-decimal pl-5 my-2 space-y-1">{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li className="leading-relaxed">{children}</li>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-semibold text-text-primary">{children}</strong>
  ),
  em: ({ children }: { children?: React.ReactNode }) => <em className="italic">{children}</em>,
  blockquote: ({ children }: { children?: React.ReactNode }) => (
    <blockquote className="border-l-2 border-accent pl-3 italic text-text-secondary my-3">
      {children}
    </blockquote>
  ),
  code: ({ children }: { children?: React.ReactNode }) => (
    <code className="font-mono text-[12px] bg-paper-card px-1 py-0.5 rounded">{children}</code>
  ),
  hr: () => <hr className="my-4 border-rule" />,
  table: ({ children }: { children?: React.ReactNode }) => (
    <table className="my-3 border-collapse border border-rule text-[12.5px]">{children}</table>
  ),
  th: ({ children }: { children?: React.ReactNode }) => (
    <th className="border border-rule bg-paper-card px-2 py-1 font-semibold text-left">
      {children}
    </th>
  ),
  td: ({ children }: { children?: React.ReactNode }) => (
    <td className="border border-rule px-2 py-1 align-top">{children}</td>
  ),
};

type Props = {
  dossierId: string;
  /** Si défini, le composant n'affiche que cette recherche (pas de sub-tabs).
   * Utilisé pour les nouveaux top-tabs distincts Synthèse / Charges /
   * Décharges / Droit dans Outils IA. */
  filterType?: ResearchType;
};

export default function AiResearchTab({ dossierId, filterType }: Props) {
  const [items, setItems] = useState<Research[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [active, setActive] = useState<ResearchType | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get<Research[]>(`/dossiers/${dossierId}/ai-research`);
      setItems(data);
      if (filterType) {
        setActive(filterType);
      } else if (!active && data.length > 0) {
        setActive(data[0]!.research_type);
      }
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [dossierId, active, filterType]);

  useEffect(() => {
    void load();
  }, [load]);

  const onForceRun = async () => {
    setRunning(true);
    try {
      // Régénère uniquement la synthèse (4 recherches + détecteurs) — pas
      // besoin de re-digérer (RAG/parties déjà à jour) depuis cet onglet.
      await api.post(`/dossiers/${dossierId}/ai-research/synthesize`, { force: true });
      toast.success('Re-synthèse en cours…');
      await load();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Échec');
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return <div className="px-6 py-5 text-[13px] text-text-secondary">Chargement…</div>;
  }

  const currentLabel = filterType ? TYPE_LABELS[filterType] : 'Synthèse IA';

  if (items.length === 0) {
    return (
      <div className="px-6 py-5 space-y-4">
        <header>
          <h1 className="font-serif text-[20px] font-medium text-text-primary">{currentLabel}</h1>
          <p className="mt-1 text-[13px] text-text-secondary">
            Aucune synthèse IA n'a encore été générée pour ce dossier. Dans le header du dossier, «
            Digère » d'abord le dossier (RAG + parties), puis clique sur « Synthétiser » pour lancer
            les 4 recherches.
          </p>
        </header>
        <div className="rounded-md border border-dashed border-rule-strong bg-paper-elevated p-5 text-center text-[13px] text-text-tertiary">
          <IconBrain size={32} stroke={1.4} className="mx-auto" />
          <p className="mt-2 font-medium text-text-primary">{currentLabel} non générée</p>
          <p className="mt-1 text-[12px]">
            L'orchestrateur Analyse IA produit 4 rapports : charges, décharges, synthèse, droit. Il
            alimente aussi les détecteurs de contradictions + cohérence + procédural, et les parties
            / dates / métadonnées.
          </p>
        </div>
      </div>
    );
  }

  const current =
    filterType !== undefined
      ? items.find((i) => i.research_type === filterType)
      : active
        ? items.find((i) => i.research_type === active)
        : items[0];

  // Si le type demandé n'existe pas encore (analyse partielle), placeholder.
  if (filterType && !current) {
    return (
      <div className="px-6 py-5 space-y-4">
        <header>
          <h1 className="font-serif text-[20px] font-medium text-text-primary">{currentLabel}</h1>
          <p className="mt-1 text-[13px] text-text-secondary">
            Cette recherche n'a pas encore été générée. Lance l'analyse IA (bouton en haut du
            dossier) ou re-lance avec force.
          </p>
        </header>
      </div>
    );
  }

  return (
    <div className="px-6 py-5 space-y-4">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-serif text-[20px] font-medium text-text-primary">{currentLabel}</h1>
          <p className="mt-1 text-[13px] text-text-secondary">
            {filterType
              ? 'Recherche IA — coche les items à réutiliser dans rapport, conclusions ou actes.'
              : '4 recherches automatiques (charges / décharges / synthèse / droit) — coche les items à réutiliser dans rapport, conclusions ou actes.'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Btn
            type="button"
            variant="primary"
            size="sm"
            onClick={() => {
              void api
                .downloadBlob(`/dossiers/${dossierId}/ai-research/export.docx`, 'synthese-ia.docx')
                .catch((e) =>
                  toast.error(e instanceof ApiError ? e.message : 'Téléchargement échoué')
                );
            }}
            title="Télécharger les 4 recherches en .docx (Word)"
          >
            <IconDownload size={14} stroke={1.6} />
            Télécharger en Word
          </Btn>
          <Btn
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => void onForceRun()}
            disabled={running}
            title="Forcer une re-analyse complète (ignore le cache incrémental)"
          >
            <IconRefresh size={14} stroke={1.6} className={running ? 'animate-spin' : ''} />
            {running ? 'En cours…' : 'Re-analyser'}
          </Btn>
        </div>
      </header>

      {!filterType && (
        <div className="flex flex-wrap gap-2 border-b border-rule pb-2">
          {ALL_TYPES.map((t) => {
            const found = items.find((i) => i.research_type === t);
            const isActive = (active ?? items[0]?.research_type) === t;
            const pickedCount = found?.checked_item_keys?.length ?? 0;
            return (
              <button
                key={t}
                type="button"
                onClick={() => setActive(t)}
                disabled={!found}
                className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-[12.5px] transition ${
                  isActive
                    ? 'border-accent bg-accent-light text-accent-text'
                    : found
                      ? 'border-rule bg-paper-elevated text-text-secondary hover:border-rule-strong'
                      : 'border-rule bg-paper-card text-text-tertiary opacity-50 cursor-not-allowed'
                }`}
              >
                {TYPE_ICONS[t]}
                <span>{TYPE_LABELS[t]}</span>
                {pickedCount > 0 && (
                  <span className="ml-1 inline-flex items-center justify-center rounded-full bg-accent px-1.5 text-[10px] font-medium text-white">
                    {pickedCount}
                  </span>
                )}
                {!found && <span className="text-[10px] italic">non générée</span>}
              </button>
            );
          })}
        </div>
      )}

      {current && (
        <ResearchBody
          key={current.id}
          dossierId={dossierId}
          research={current}
          onPicksUpdated={(updated) => {
            setItems((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
          }}
        />
      )}
    </div>
  );
}

function ResearchBody({
  dossierId,
  research,
  onPicksUpdated,
}: {
  dossierId: string;
  research: Research;
  onPicksUpdated: (r: Research) => void;
}) {
  const parsed = useMemo(
    () => parseResearchItems(research.content_markdown),
    [research.content_markdown]
  );

  const [checked, setChecked] = useState<Set<string>>(new Set(research.checked_item_keys));
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    setChecked(new Set(research.checked_item_keys));
  }, [research.id, research.checked_item_keys]);

  const sync = useCallback(
    (next: Set<string>) => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      debounceRef.current = window.setTimeout(() => {
        const body = { checked: Array.from(next) };
        api
          .patch<Research>(`/dossiers/${dossierId}/ai-research/${research.id}/picks`, body)
          .then((updated) => onPicksUpdated(updated))
          .catch((e) => {
            toast.error(e instanceof ApiError ? e.message : 'Échec de la sélection');
          });
      }, 350);
    },
    [dossierId, research.id, onPicksUpdated]
  );

  const toggle = (key: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      sync(next);
      return next;
    });
  };

  return (
    <article className="space-y-4">
      <div className="flex items-baseline gap-3 text-[11.5px] text-text-tertiary flex-wrap">
        <span>
          Généré le{' '}
          {new Date(research.created_at).toLocaleString('fr-BE', {
            dateStyle: 'short',
            timeStyle: 'short',
          })}
        </span>
        <span>· {research.pdf_count_at_run} pièce(s) au moment du run</span>
        {research.cost_eur !== null && <span>· {research.cost_eur.toFixed(3)} €</span>}
        {research.tokens_in && (
          <span>
            · {research.tokens_in.toLocaleString('fr-BE')} tokens in /{' '}
            {(research.tokens_out ?? 0).toLocaleString('fr-BE')} out
          </span>
        )}
        {parsed.items.length > 0 && (
          <span>
            · {checked.size}/{parsed.items.length} item(s) sélectionné(s)
          </span>
        )}
      </div>

      {parsed.items.length > 0 ? (
        <div className="space-y-3">
          {parsed.preamble && (
            <div className="rounded-md border border-rule bg-paper-elevated p-4 text-[13px] leading-relaxed text-text-primary">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
                {parsed.preamble}
              </ReactMarkdown>
            </div>
          )}
          {parsed.items.map((item) => {
            const isChecked = checked.has(item.key);
            return (
              <label
                key={item.key}
                className={`flex gap-3 rounded-md border p-4 text-[13px] leading-relaxed cursor-pointer transition ${
                  isChecked
                    ? 'border-accent bg-accent-lighter'
                    : 'border-rule bg-paper-elevated hover:border-rule-strong'
                }`}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggle(item.key)}
                  className="mt-1 h-4 w-4 accent-accent flex-none"
                />
                <div className="flex-1 min-w-0 text-text-primary">
                  <h3 className="font-semibold mb-1.5">{item.title}</h3>
                  <div className="text-[13px] leading-relaxed">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
                      {item.body}
                    </ReactMarkdown>
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      ) : (
        <div className="rounded-md border border-rule bg-paper-elevated p-4">
          <div className="text-[13px] leading-relaxed text-text-primary">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
              {research.content_markdown}
            </ReactMarkdown>
          </div>
        </div>
      )}

      {research.sources.length > 0 && (
        <details className="rounded-md border border-rule bg-paper-card p-3 text-[12px]">
          <summary className="cursor-pointer font-medium text-text-primary">
            Sources utilisées ({research.sources.length})
          </summary>
          <ul className="mt-2 space-y-1 text-text-secondary">
            {research.sources.map((s, idx) => {
              const isCorpus = (s as { kind?: string }).kind === 'corpus';
              if (isCorpus) {
                const c = s as Extract<ResearchSource, { kind: 'corpus' }>;
                return (
                  <li key={`corpus-${c.source_id}-${idx}`}>
                    <span className="font-mono text-[10.5px] text-text-tertiary">
                      {(c.score * 100).toFixed(0)}%
                    </span>{' '}
                    <span className="inline-flex items-center rounded-pill bg-accent-lighter px-1.5 py-[1px] text-[10px] font-medium uppercase tracking-[0.3px] text-accent-text">
                      {c.source_type === 'doctrine'
                        ? 'doctrine'
                        : c.source_type === 'jurisprudence_cass'
                          ? 'cass'
                          : c.source_type === 'code_legal'
                            ? 'CIC/CP'
                            : 'modèle'}
                    </span>{' '}
                    {c.title}
                    {c.author && <> — {c.author}</>}
                    {c.year && <> ({c.year})</>}
                    {c.locator && (
                      <span className="font-mono text-[10.5px] text-text-tertiary">
                        {' '}
                        · {c.locator}
                      </span>
                    )}
                  </li>
                );
              }
              const d = s as Extract<ResearchSource, { kind?: 'dossier' }>;
              return (
                <li key={`dossier-${d.pdf_id}-${d.page}-${idx}`}>
                  <span className="font-mono text-[10.5px] text-text-tertiary">
                    {(d.score * 100).toFixed(0)}%
                  </span>{' '}
                  <span className="inline-flex items-center rounded-pill bg-paper-subtle px-1.5 py-[1px] text-[10px] font-medium uppercase tracking-[0.3px] text-text-tertiary">
                    dossier
                  </span>{' '}
                  {d.filename} · page {d.page}
                </li>
              );
            })}
          </ul>
        </details>
      )}
    </article>
  );
}
